import springyModel from '../../tools/SpringyModel.js';

/**
  * Operator/librarian session object.
  */
const opSession = Object.create(springyModel);
/**
  * Defaults for ops
  * @prop {String} sessionId LA User Unique id.
  * @prop {String} name Nickname to show to patrons.
  * @prop {String} fullname Name to show to other ops.
  * @prop {String} role always "op"
  * @prop {Number} opId Op's LA userid number.
  * @prop {Array} consortium Array of consortial ids.
  * @prop {Array} coopIds Coop ids this op is monitoring (this is not reset on disconnect).
  * @prop {String} siteName Name of their associated site.
  * @prop {Number} iid Site IID.
  * @prop {Boolean} isAway Currently marked as away?
  * @prop {Object} profile_image Image path and color props.
  * @prop {Array} deptsMonitored Currently logged in department ids (this is not reset on disconnect).
  * @prop {Array} deptsInternal Department ids that user belongs to (regardless of current status). Used for internal. Should not change.
  * @prop {String} ip Ip address.
  * @prop {String} joined When the op logged in (ISO 8601 format).
  * @prop {Boolean} isConnected Is op online (so object can stick around after disconnect)
  * @prop {Array} currentUserChats Sessionids for users this op is talking to. (For the current dashboard op only.)
  * @prop {String} region Region code for op's region.
  * @prop {Number} unitId Consortial unit id.
  * @prop {String} unitName Consortial unit name.
  */
opSession.defaults = {
    sessionId: '',
    name: '',
    fullname: '',
    role: 'op',
    opId: 0,
    consortium: [],
    coopIds: [],
    siteName: '',
    iid: 0,
    isAway: false,
    profile_image: { path: '', color: '' },
    deptsMonitored: [],
    deptsInternal: [],
    ip: '',
    joined: '',
    isConnected: false,
    currentUserChats: [],
    region: '',
    unitId: 0,
    unitName: '',
};
/**
  * @prop {Boolean} isShared Is this op from a shared site (not the current op's own).
  */
opSession.isShared = false;
/**
  * @prop {Number|null} awayTimeout Timeout function id for assuming a pending op is staying away.
  */
opSession.awayTimeout = null;

/**
  * Wrapper for springyModel.init
  * @param {Object} config Data for the model
  * @param {EventEmitter} emitter Event emitter.
  */
opSession.initialize = function (config, emitter) {
    // Make sure we have the fullname prop set
    // even if it has to be set to the nickname.
    // necessary sometimes in Co-Ops.
    if (!config.fullname || config.fullname === '') {
        config.fullname = config.name || '';
    }
    this.init(config, emitter);
};
/**
  * Chat room for chats to individual op.
  * @returns {String}
  */
opSession.getRoom = function () {
    return `internal-${this.get('sessionId')}`;
};
/**
  * Status text for indicators and such.
  * @returns {String}
  */
opSession.getStatusText = function () {
    const isConnected = this.get('isConnected');
    const isAway = this.get('isAway');
    if (!isConnected) {
        return 'offline';
    }
    if (isAway) {
        return 'away';
    }
    if (this.get('deptsMonitored').length === 0 && this.get('coopIds').length === 0) {
        return 'internal';
    }
    return 'online';
};
/**
  * Add op to department
  * @param {Number} deptId Department id.
  */
opSession.addDept = function (deptId) {
    if (!this.get('deptsMonitored').includes(deptId)) {
        this.get('deptsMonitored').push(deptId);
    }
};
/**
  * Remove op from department
  * @param {Number} deptId chat department
  */
opSession.removeDept = function (deptId) {
    const index = this.get('deptsMonitored').indexOf(deptId);
    if (index !== -1) {
        this.get('deptsMonitored').splice(index, 1);
    }
};
/**
  * Is the op monitoring a dept.
  * @param {Number} deptId chat department
  * @returns {Boolean}
  */
opSession.isMonitoringDept = function (deptId) {
    return (this.get('deptsMonitored').indexOf(deptId) !== -1);
};
/**
  * Add op to coop
  * @param {String} coopId Coop id.
  */
opSession.addCoop = function (coopId) {
    if (!this.get('coopIds').includes(coopId)) {
        this.get('coopIds').push(coopId);
    }
};
/**
  * Remove op from Coop
  * @param {String} coopId chat Coop
  */
opSession.removeCoop = function (coopId) {
    const index = this.get('coopIds').indexOf(coopId);
    if (index !== -1) {
        this.get('coopIds').splice(index, 1);
    }
};
/**
  * Is the op monitoring a coop.
  * @param {String} coopId Coop id.
  * @returns {Boolean}
  */
opSession.isMonitoringCoop = function (coopId) {
    return (this.get('coopIds').indexOf(coopId) !== -1);
};
/**
  * When op starts chatting to a user.
  * @param {String} sessionId User op is chatting to.
  */
opSession.addUser = function (sessionId) {
    const currentUserChats = this.get('currentUserChats');
    const index = currentUserChats.indexOf(sessionId);
    if (index === -1) {
        currentUserChats.push(sessionId);
    }
};
/**
  * When op stops chatting to a user.
  * @param {String} sessionId User op is chatting to.
  */
opSession.removeUser = function (sessionId) {
    const currentUserChats = this.get('currentUserChats');
    const index = currentUserChats.indexOf(sessionId);
    if (index !== -1) {
        currentUserChats.splice(index, 1);
    }
};
/**
  * Update some op data.
  * For when we have op data but cascade sends us the op again for some reason.
  * This just changes fields that are possible/likely to change after the op was created.
  * @param {Object} opData Data object from cascade.
  */
opSession.updateFromServer = function (opData) {
    // Aren't always getting ALL these fields.
    const props = {};
    if (opData.sessionId) {
        props.sessionId = opData.sessionId;
    }
    if (opData.deptsMonitored) {
        props.deptsMonitored = opData.deptsMonitored;
    }
    if (opData.coopIds) {
        props.coopIds = opData.coopIds;
    }
    if (typeof opData.isAway !== 'undefined') {
        props.isAway = opData.isAway;
    }
    if (typeof opData.isConnected !== 'undefined') {
        props.isConnected = opData.isConnected;
    }
    if (opData.joined) {
        props.joined = opData.joined;
    }
    if (opData.profile_image) {
        props.profile_image = opData.profile_image;
    }
    this.set(props);
};
/**
  * Set a timout for pending/away ops to trigger an event.
  * Generally, this would be triggering a disconnected type change.
  * @param {Function} callback Callback to execute.
  */
opSession.setPendingTimeout = function (callback) {
    window.clearTimeout(this.awayTimeout);
    this.awayTimeout = window.setTimeout(callback, 60000);
};
/**
  * Clear the pending/away timeout if op reconnects.
  */
opSession.clearPendingTimeout = function () {
    window.clearTimeout(this.awayTimeout);
};
/**
  * Override parent so we can set up-to-date status for templates.
  * @returns {Object}
  */
opSession.toJSON = function () {
    // Call prototypes method
    const obj = springyModel.toJSON.call(this);
    // so we can get the current status
    obj.status = this.getStatusText();
    return obj;
};

export default opSession;
